/**
 * Settings > General > Sidebar: which tabs the firm's sidebar shows. A hidden
 * tab only leaves the sidebar; its page still opens by link. Owners and
 * admins change it, a switch saving as it flips, and at least one tab every
 * member sees stays on. Settings is in the account menu, so it is never here.
 */
import { useEffect, useMemo, useState, type FC } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { LawFirmsAPI } from "@/api/lawFirms";
import type { LawFirm } from "@/api/types/lawFirm";
import {
  SIDEBAR_TABS,
  canHideSidebarTab,
  withSidebarTab,
  type SidebarTab
} from "@/components/app-sidebar/sidebarTabs";
import { ListCard, ListRow } from "@/components/ui/list-card";
import { SectionHeader } from "@/components/ui/page-header";
import { Switch } from "@/components/ui/switch";
import { announceFirmUpdated } from "@/features/lawFirms/firmEvents";
import { lawFirmKey } from "@/features/lawFirms/firmQuery";
import { queryClient } from "@/lib/query/queryClient";
import { lawyerSelectors } from "@/redux/lawyer/selectors";

type Props = {
  lawFirm: LawFirm | null;
  onSaved: (firm: LawFirm) => void;
};

/** The tabs this firm's sidebar can show at all. */
export const listedSidebarTabs = ({
  timeTracking,
  athena
}: {
  timeTracking: boolean;
  athena: boolean;
}): SidebarTab[] =>
  SIDEBAR_TABS.filter((tab) => {
    if (tab.id === "time" || tab.id === "billing-review") return timeTracking;
    if (tab.id === "athena") return athena;
    return true;
  });

export const FirmSidebarSection: FC<Props> = ({ lawFirm, onSaved }) => {
  const timeTracking = useSelector(lawyerSelectors.selectTimeTrackingEnabled);
  const tabs = useMemo(
    () =>
      listedSidebarTabs({
        timeTracking: Boolean(timeTracking),
        athena: import.meta.env.REACT_APP_ENABLE_PLAYGROUND === "true"
      }),
    [timeTracking]
  );
  const saved = lawFirm?.hidden_sidebar_tabs;
  const [hidden, setHidden] = useState<string[]>(saved ?? []);
  const [saving, setSaving] = useState(false);

  useEffect(() => setHidden(saved ?? []), [saved]);

  const setShown = async (id: string, show: boolean) => {
    if (!lawFirm) return;
    const previous = hidden;
    const next = withSidebarTab(hidden, id, show);
    setHidden(next);
    setSaving(true);
    try {
      const updated = await LawFirmsAPI.update(lawFirm.uid, {
        hidden_sidebar_tabs: next
      });
      onSaved(updated);
      const key = lawFirmKey(lawFirm.uid);
      queryClient.setQueryData<LawFirm>(key, (firm) =>
        firm
          ? {
              ...firm,
              hidden_sidebar_tabs: updated.hidden_sidebar_tabs ?? next
            }
          : firm
      );
      void queryClient.invalidateQueries({ queryKey: key });
      announceFirmUpdated(lawFirm.uid);
      toast.success("Sidebar saved");
    } catch (error) {
      setHidden(previous);
      toast.error("Couldn't save the sidebar", {
        description:
          error instanceof Error
            ? error.message
            : "Only owners and admins can change it."
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex w-full flex-col gap-3" data-testid="firm-sidebar">
      <SectionHeader
        title="Sidebar"
        description="Choose the tabs everyone at your firm sees in the sidebar. Hidden pages still open from links."
      />
      <ListCard>
        {tabs.map((tab) => {
          const shown = !hidden.includes(tab.id);
          const locked = shown && !canHideSidebarTab(hidden, tab.id);
          return (
            <ListRow
              key={tab.id}
              title={tab.label}
              description={
                locked ? (
                  <span className="text-sm text-fg-3">
                    Keep at least one tab in the sidebar.
                  </span>
                ) : undefined
              }
              control={
                <Switch
                  aria-label={`Show ${tab.label} in Sidebar`}
                  data-testid={`sidebar-tab-${tab.id}`}
                  checked={shown}
                  disabled={!lawFirm || saving || locked}
                  onCheckedChange={(on) => void setShown(tab.id, on)}
                />
              }
            />
          );
        })}
      </ListCard>
    </section>
  );
};
